import { Injectable } from "@angular/core";
import { Subject, Subscription } from "rxjs";
import { OEScreenRegistryData } from "../../data/models/oe-screen-registry.model";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OEModelGetterService } from "../model/oe-model-getter.service";
import { OENDisplayState } from "../../data/enums/oe-display-state.enum";
import { OELoggerService } from "./oe-logger.service";

@Injectable()
export class OEScreenRegistryService {
  /**********************************************************************/
  /* DATA */
  private oeScreenRegistryMap: Map<string, OEScreenRegistryData> = new Map<
    string,
    OEScreenRegistryData
  >();

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(protected oeLogger: OELoggerService, protected getter: OEModelGetterService) {
    this.oeLogger = oeLogger.for(__dirname);
  }

  /**********************************************************************/
  /* Screen Registry Management */

  // for use in oe-services
  public get(refId: string): OEScreenRegistryData | null {
    if (this.has(refId)) {
      return this.oeScreenRegistryMap.get(refId);
    }
    return null;
  }

  // for use in oe-services
  public has(refId: string): boolean {
    if (this.getter.validateRefFormat(refId)) {
      return this.oeScreenRegistryMap.has(refId) || this.findScreenInData(refId) != null;
    } else {
      logger.out.warn(
        `WARN: ${this.constructor.name}.has(): Invalid input paramter! (refId -> '${refId}')`,
      );
    }
    return false;
  }

  // reference only in oe-api.service for use by panel components, not in other oe-services
  public register(refId: string): void {
    if (this.getter.validateRefFormat(refId)) {
      let refIdKey = this.oeScreenRegistryMap.has(refId) ? refId : this.findScreenInData(refId);
      if (refIdKey == null) {
        if (this.getter.has(refId)) {
          let screenData = this.generateScreenData(refId);
          if (screenData instanceof OEScreenRegistryData) {
            this.oeScreenRegistryMap.set(screenData.rootRefId, screenData);
            this.oeLogger.outputDevLogToConsole(
              `OE-SCREEN-REGISTRY: NEW: [${screenData.rootRefId}]  `,
              false,
              screenData,
            );
          } else {
            logger.out.warn(
              `WARN: ${this.constructor.name}.register(): Failed to register screen! (refId -> '${refId}')`,
            );
          }
        } else {
          logger.out.warn(
            `WARN: ${this.constructor.name}.register(): Model not found! (refId -> '${refId}')`,
          );
        }
      } else {
        this.oeLogger.outputExtendedLogging(
          `INFO: ${this.constructor.name}.register(): Already Registered!  (refId -> '${refId}')`,
        );
      }
    } else {
      logger.out.warn(
        `WARN: ${this.constructor.name}.register(): Invalid input parameter! (refId -> '${refId}')`,
      );
    }
  }

  // reference only in oe-api.service for use by panel components, not in other oe-services
  public unregister(refId: string): boolean {
    if (this.getter.validateRefFormat(refId)) {
      if (this.oeScreenRegistryMap.has(refId)) {
        this.oeScreenRegistryMap.delete(refId);
        this.oeLogger.outputDevLogToConsole(`OE-SCREEN-REGISTRY: DELETE: [${refId}] `);
        return true;
      } else {
        let refIdKey = this.findScreenInData(refId);
        if (refIdKey != null) {
          this.oeScreenRegistryMap.delete(refIdKey);
          this.oeLogger.outputDevLogToConsole(`OE-SCREEN-REGISTRY: DELETE: [${refIdKey}] `);
          return true;
        }
      }
    } else {
      logger.out.warn(
        `WARN: ${this.constructor.name}.unregister(): Invalid input paramter! (refId -> '${refId}')`,
      );
    }
    return false;
  }

  // reference only in oe-api.service for use by panel components, not in other oe-services
  public preregister(originalRefId: string, newRefId: string): void {
    this.oeLogger.outputDevLogToConsole(
      `OE-SCREEN-REGISTRY: PREREGISTER [${originalRefId} -> ${newRefId}]`,
    );
    if (this.unregister(originalRefId)) {
      this.register(newRefId);
    } else {
      logger.out.warn(
        `WARN: ${this.constructor.name}.preregister(): Failed to unregister ${originalRefId} as it is not in registry`,
      );
    }
  }

  public updateFull(refId: string): void {
    if (this.getter.validateRefFormat(refId)) {
      let refIdKey = this.oeScreenRegistryMap.has(refId) ? refId : this.findScreenInData(refId);
      if (refIdKey != null) {
        let screenData = this.generateScreenData(refIdKey);
        if (screenData instanceof OEScreenRegistryData) {
          this.oeScreenRegistryMap.set(refIdKey, screenData);
          this.oeLogger.outputDevLogToConsole(
            `OE-SCREEN-REGISTRY: UPDATE-FULL: [${refIdKey}] `,
            false,
            screenData,
          );
        } else {
          logger.out.warn(
            `WARN: ${this.constructor.name}.updateFull(): Failed to update Data! (refId -> '${refId}')`,
          );
        }
      } else {
        logger.out.warn(
          `WARN: ${this.constructor.name}.updateFull(): Screen not in registry! (refId -> '${refId}')`,
        );
      }
    } else {
      logger.out.warn(
        `WARN: ${this.constructor.name}.updateFull(): Invalid input paramter! (refId -> '${refId}')`,
      );
    }
  }

  public updateDisplay(refId: string, displayState: OENDisplayState): void {
    if (this.getter.validateRefFormat(refId) && displayState in OENDisplayState) {
      let refIdKey = this.oeScreenRegistryMap.has(refId) ? refId : this.findScreenInData(refId);
      if (
        refIdKey != null &&
        this.oeScreenRegistryMap.get(refIdKey) instanceof OEScreenRegistryData
      ) {
        this.oeScreenRegistryMap.get(refIdKey).displayState = displayState;
        this.oeLogger.outputDevLogToConsole(
          `OE-SCREEN-REGISTRY: UPDATE-DISPLAY: [${refIdKey}] -> ${OENDisplayState[displayState]}`,
        );
      } else {
        logger.out.warn(
          `WARN: ${this.constructor.name}.updateDisplay(): Failed to update Display! (refId -> '${refId}')`,
        );
      }
    } else {
      logger.out.warn(
        `WARN: ${this.constructor.name}.updateDisplay(): Invalid input paramter! (refId -> '${refId}')`,
      );
    }
  }

  /**********************************************************************/
  /* Helpers */
  private findScreenInData(refId: string): string | null {
    if (this.getter.validateRefFormat(refId) && this.oeScreenRegistryMap.size > 0) {
      if (this.oeScreenRegistryMap.has(refId)) {
        // single node
        return refId;
      }
      for (let [refIdKey, screenData] of this.oeScreenRegistryMap) {
        // strategy
        if (typeof refIdKey === "string" && screenData instanceof OEScreenRegistryData) {
          if (Array.isArray(screenData.refIds) && screenData.refIds.length > 1) {
            // root always present
            let result = screenData.refIds.find((itemId) => itemId === refId);
            if (result != null) {
              return refIdKey;
            }
          }
        } else {
          logger.out.warn(
            `WARN: ${this.constructor.name}.findScreenInData(): Invalid registry entry! (key -> ${refIdKey})`,
          );
        }
      }
    }
    return null;
  }

  private generateScreenData(refId: string): OEScreenRegistryData | null {
    try {
      if (this.getter.has(refId)) {
        let screenData = new OEScreenRegistryData();
        screenData.rootRefId = this.getter.getRootIdForNodeWithRefId(refId);
        screenData.refIds = this.getter.getStrategyNodesListRoot(refId);
        screenData.entryType = this.getter.getEntryType(refId);
        screenData.operationMode = this.getter.getOperationMode(refId);
        screenData.displayState = this.getter.getDisplayState(refId);
        return screenData;
      }
    } catch (ex) {
      logger.out.warn(`WARN: ${this.constructor.name}.generateScreenData(): Exception: ${ex}`);
    }
    return null;
  }
}
